import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import type { UserPrimaryRole } from '@/types'
import { LoadingScreen } from '@/components/ui/LoadingScreen'

interface ProtectedRouteProps {
  children: React.ReactNode
  requiredRole: UserPrimaryRole
}

// Map each role to its home portal path
const ROLE_HOME: Record<UserPrimaryRole, string> = {
  school_admin: '/app/school',
  instructor: '/app/instructor',
  student: '/app/student',
  drivetrack_admin: '/app/admin',
}

export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { session, profile, instructorProfile, schoolProfileRequired, loading, profileLoading } = useAuth()
  const location = useLocation()

  // While initial auth state resolves, show a blank loading screen
  if (loading || profileLoading) {
    return <LoadingScreen />
  }

  // Not authenticated — redirect to login, preserving the intended destination.
  // drivetrack_admin has its own dedicated login page (no Google OAuth, no self-register
  // link) since admins never self-register — only accept an invite.
  if (!session) {
    const loginPath = requiredRole === 'drivetrack_admin' ? '/admin/login' : '/login'
    return <Navigate to={`${loginPath}?redirect=${encodeURIComponent(location.pathname)}`} replace />
  }

  // Authenticated but no profile yet (e.g. brand-new Google user who hasn't selected a role)
  if (!profile) {
    return <Navigate to='/auth/select-role' replace />
  }

  // Profile exists but role is not set (same case — new Google OAuth user)
  if (!profile.primary_role) {
    return <Navigate to='/auth/select-role' replace />
  }

  // Wrong role — redirect to the user's actual portal instead of showing an error
  if (profile.primary_role !== requiredRole) {
    const correctHome = ROLE_HOME[profile.primary_role]
    return <Navigate to={correctHome} replace />
  }

  // Instructor onboarding gate: instructor must complete their profile before
  // accessing any part of the instructor portal. The check uses the server-
  // authoritative onboarding_completed_at column — never localStorage.
  // Skip the gate when already on the onboarding page to avoid redirect loops.
  if (
    profile.primary_role === 'instructor' &&
    requiredRole === 'instructor' &&
    location.pathname !== '/app/instructor/onboarding' &&
    instructorProfile?.onboarding_completed_at == null
  ) {
    return <Navigate to='/app/instructor/onboarding' replace />
  }

  // School business-profile onboarding gate (migration 080): a school_admin who OWNS a
  // school still gated by the required wizard cannot reach the rest of the school portal.
  // schoolProfileRequired is server-authoritative (AuthContext, from schools.
  // requires_profile_wizard/profile_completed_at) — never true for an existing,
  // backfilled-exempt school. Skip when already on the wizard route to avoid a loop.
  if (
    profile.primary_role === 'school_admin' &&
    requiredRole === 'school_admin' &&
    location.pathname !== '/app/school/onboarding-wizard' &&
    schoolProfileRequired === true
  ) {
    return <Navigate to='/app/school/onboarding-wizard' replace />
  }

  // Suspended account
  if (profile.account_status === 'suspended') {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', background: '#FAF6EF', padding: '24px',
      }}>
        <div style={{
          maxWidth: 440, background: '#fff', borderRadius: 16,
          padding: '32px 28px', border: '1px solid rgba(224,102,59,0.3)',
          textAlign: 'center',
        }}>
          <div style={{ font: '600 20px Lora,serif', color: '#1B2A4A', marginBottom: 10 }}>
            Account suspended
          </div>
          <div style={{ font: '400 14px Inter,sans-serif', color: 'rgba(27,42,74,0.65)' }}>
            Your account has been suspended. Please contact support.
          </div>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
